import { TZDate } from '@date-fns/tz';
import { format, formatDistanceToNow } from 'date-fns';

import { MILLISECONDS_IN_DAY } from '../time.constants';

type DateInput = Date | number | string;

const toDate = (date: DateInput): Date => (date instanceof Date ? date : new Date(date));

export const addUtcDays = (date: DateInput, days: number): Date => {
    const value = toDate(date);
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate() + days));
};

export const startOfUtcDay = (date: DateInput): Date => addUtcDays(date, 0);

const endOfUtcDay = (date: DateInput): Date => {
    const start = startOfUtcDay(date);
    return new Date(start.getTime() + MILLISECONDS_IN_DAY - 1);
};

export const startOfUtcDayIso = (date: DateInput): string => startOfUtcDay(date).toISOString();

export const endOfUtcDayIso = (date: DateInput): string => endOfUtcDay(date).toISOString();

export const localStartOfDayAsUtcIso = (date: DateInput): string => {
    const value = toDate(date);
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())).toISOString();
};

export const localEndOfDayAsUtcIso = (date: DateInput): string => {
    const value = toDate(date);
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), 23, 59, 59, 999)).toISOString();
};

const getUserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

const isValidTimeZone = (timeZoneId?: string): timeZoneId is string => {
    if (!timeZoneId) {
        return false;
    }

    try {
        Intl.DateTimeFormat(undefined, { timeZone: timeZoneId }).format(new Date());
        return true;
    } catch {
        return false;
    }
};

const getDateInTimeZone = (date: DateInput, timeZoneId?: string): TZDate =>
    new TZDate(toDate(date), isValidTimeZone(timeZoneId) ? timeZoneId : getUserTimeZone());

export const startOfTimeZoneDayAsUtcIso = (date: DateInput, timeZoneId?: string): string => {
    const zonedDate = getDateInTimeZone(date, timeZoneId);
    return new Date(Date.UTC(zonedDate.getFullYear(), zonedDate.getMonth(), zonedDate.getDate())).toISOString();
};

export const utcDateFieldsAsTimeZoneIso = (date: DateInput, timeZoneId: string): string => {
    const value = toDate(date);

    return new TZDate(
        value.getUTCFullYear(),
        value.getUTCMonth(),
        value.getUTCDate(),
        value.getUTCHours(),
        value.getUTCMinutes(),
        value.getUTCSeconds(),
        value.getUTCMilliseconds(),
        timeZoneId
    ).toISOString();
};

export const formatInTimeZone = (date: DateInput, timeZoneId: string | undefined, dateFormat: string): string =>
    format(getDateInTimeZone(date, timeZoneId), dateFormat);

export const formatFromNow = (date: DateInput): string => formatDistanceToNow(toDate(date), { addSuffix: true });
